import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, MessageSquare, Download, Clock, Users, Utensils } from "lucide-react";
import { useSavedRecipes, SavedRecipe } from "@/hooks/useSavedRecipes";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { NutritionIcons } from "./NutritionIcons";
import { RecipeDetailModal } from "./RecipeDetailModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const RecipeBrowser = () => {
  const { t } = useTranslation();
  const { recipes, loading, deleteRecipe, exportShoppingList, importRecipeAsMeal } = useSavedRecipes();
  const navigate = useNavigate();
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [recipeToDelete, setRecipeToDelete] = useState<string | null>(null);
  const [selectedRecipeDetail, setSelectedRecipeDetail] = useState<SavedRecipe | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const toggleRecipeSelection = (recipeId: string) => {
    setSelectedRecipes(prev => 
      prev.includes(recipeId) 
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const handleExportShoppingList = () => {
    if (selectedRecipes.length === 0) {
      return;
    }
    exportShoppingList(selectedRecipes);
  };

  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) return;
    await deleteRecipe(recipeToDelete);
    setRecipeToDelete(null);
    setSelectedRecipes(prev => prev.filter(id => id !== recipeToDelete));
  };

  const handleRecipeClick = (recipe: SavedRecipe) => {
    setSelectedRecipeDetail(recipe);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-normal">{t('components.recipes.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('components.recipes.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {selectedRecipes.length > 0 && (
            <Button
              onClick={handleExportShoppingList}
              variant="outline"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {t('components.recipes.shoppingListBtn', { count: selectedRecipes.length })}
            </Button>
          )}
          <Button
            onClick={() => navigate('/chat')}
            className="bg-gradient-primary text-black gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            {t('components.recipes.askCoach')}
          </Button>
        </div>
      </div>

      {/* Recipes Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">{t('components.recipes.loading')}</p>
        </div>
      ) : recipes.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="h-12 w-12 mx-auto text-primary mb-4" />
          <h3 className="text-xl font-normal mb-2">{t('components.recipes.empty')}</h3>
          <p className="text-muted-foreground mb-6">
            {t('components.recipes.emptyHint')}<br />
            {t('components.recipes.emptyExample')}
          </p>
          <Button
            onClick={() => navigate('/chat')}
            className="bg-gradient-primary"
          >
            {t('components.recipes.chatWithCoach')}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recipes.map((recipe) => {
            const totalTime = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);
            
            return (
              <Card key={recipe.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-4 cursor-pointer" onClick={() => handleRecipeClick(recipe)}>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-lg flex-1 line-clamp-2">{recipe.title}</h3>
                    <div className="flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRecipes.includes(recipe.id)}
                        onCheckedChange={() => toggleRecipeSelection(recipe.id)}
                        aria-label={t('components.recipes.selectForShoppingList')}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecipeToDelete(recipe.id);
                        }}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {recipe.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {recipe.description}
                    </p>
                  )}

                  {/* Macros */}
                  <div className="mb-3 p-3 bg-gradient-card rounded-lg">
                    <NutritionIcons
                      calories={recipe.calories}
                      protein={recipe.protein}
                      carbs={recipe.carbs}
                      fat={recipe.fat}
                      className="flex-wrap justify-center"
                      iconSize={16}
                    />
                  </div>

                  {/* Additional Info */}
                  <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                    {totalTime > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t('components.recipes.minutesShort', { count: totalTime })}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {t('components.recipes.servingsCount', { count: recipe.servings })}
                    </span>
                  </div>

                  {/* Ingredients Preview */}
                   {recipe.ingredients && recipe.ingredients.length > 0 && (
                    <div className="text-xs text-muted-foreground mb-3">
                      <p className="font-medium mb-1">{t('components.recipes.ingredientsCount', { count: recipe.ingredients.length })}</p>
                      <p className="line-clamp-2">
                        {recipe.ingredients.slice(0, 3).map(ing => ing.ingredient_name).join(', ')}
                        {recipe.ingredients.length > 3 && '...'}
                      </p>
                    </div>
                  )}

                  {/* Import as Meal */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full gap-2" 
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Utensils className="h-4 w-4" />
                        {t('components.recipes.importAsMeal')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-50 bg-background border border-border shadow-lg">
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'breakfast')}>
                        {t('components.nutritionDashboard.meals.breakfast')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'lunch')}>
                        {t('components.nutritionDashboard.meals.lunch')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'dinner')}>
                        {t('components.nutritionDashboard.meals.dinner')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'snack')}>
                        {t('components.nutritionDashboard.meals.snacks')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!recipeToDelete} onOpenChange={() => setRecipeToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('components.recipes.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.recipes.deleteBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRecipe} className="bg-destructive hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recipe Detail Modal */}
      <RecipeDetailModal
        recipe={selectedRecipeDetail}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedRecipeDetail(null);
        }}
      />
    </div>
  );
};
